import { useEffect, useMemo, useRef, useState } from "react";

export type DropdownOption = {
    value: string;
    label: string;
    description?: string | null;
    imageUrl?: string | null;
};

type Params = {
    value: string;
    onChange: (val: string) => void;
    options: DropdownOption[];
};

export const useDropdownMedia = ({ value, onChange, options }: Params) => {
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState<number>(-1);

    const btnRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const selected = useMemo(
        () => options.find((o) => o.value === value) || null,
        [options, value]
    );

    const close = () => setOpen(false);

    // outside click
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (btnRef.current?.contains(e.target as Node)) return;
            if (listRef.current?.contains(e.target as Node)) return;
            close();
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    // active index when menu opens or value/options change
    useEffect(() => {
        if (!open) {
            setActiveIdx(-1);
            return;
        }
        const idx = Math.max(0, options.findIndex((o) => o.value === value));
        setActiveIdx(idx === -1 ? 0 : idx);
    }, [open, options, value]);

    const commit = (idx: number) => {
        const opt = options[idx];
        if (!opt) return;
        onChange(opt.value);
        close();
        btnRef.current?.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
            return;
        }
        if (!open) return;
        if (e.key === "Escape") {
            e.preventDefault();
            close();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % options.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + options.length) % options.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            commit(activeIdx === -1 ? 0 : activeIdx);
        }
    };

    return {
        open,
        activeIdx,
        selected,
        btnRef,
        listRef,
        setOpen,
        setActiveIdx,
        close,
        commit,
        onKeyDown,
    };
};
